import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import { INVOICES_BY_VENDOR, UPDATE_INVOICE_STATUS, UPDATE_INVOICE_STATUS_BULK, NSP_CORE_INFO_BY_TYPE } from 'graphql/invoices.gql';
import { useInvoiceDownloadAudit } from '@hooks/graphql/useInvoiceDownloadAudit';
import {
  useVendorInvoiceSendJobs,
  isActiveSendJob,
  invoiceRowKey,
  vendorPrimaryName,
  summarizeVendorInvoiceSend,
} from '@hooks/graphql/useVendorInvoiceSendJobs';
import type { VendorInvoice, SaveFeedback, InvoiceSendProgress, VendorInvoiceSendJob } from '@custom-types/invoices';
import useSession from '@hooks/useSession';
import { generateNspInvoicesZip } from '@utils/invoice-generator';
import { generateVendorInvoicesZip } from '@utils/invoice-generator/vendorInvoicesZip';
import { appInsights } from '../../appInsights';

type VendorInvoiceUpdate = {
  ippId: number;
  monthYear: string;
  invoiceStatus: string;
  approvalStatus: string;
  primaryVendorName?: string;
};

// A send started from this page: the rows it covers and the caller waiting for it to finish.
type InPageSend = {
  vendors: VendorInvoice[];
  sendRunId: string;
  startedAt: number;
  resolve: (keepSelectedVendorIds: number[]) => void;
};

interface InvoicePendingChanges {
  invoiceStatus?: string;
  approvalStatus?: string;
  invoiceApproved?: boolean;
  invoiceComputedApprovalSignature?: string;
}

export const useInvoicesGraphQl = ({ monthYear }: { monthYear?: string }) => {
  const [vendorData, setVendorData] = useState<VendorInvoice[]>([]);
  const [originalData, setOriginalData] = useState<VendorInvoice[]>([]); // Track original server data
  const [pendingChanges, setPendingChanges] = useState<Record<string, InvoicePendingChanges>>({});
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const { account } = useSession();

  // Vendor invoice sends run on the server (they survive a refresh, closed tab or logout).
  const { sendJobs, startSendJob, dismissSendJob } = useVendorInvoiceSendJobs();
  // Sends started from this page, by job id, until they finish
  const inPageSends = useRef(new Map<number, InPageSend>());
  // Finished sends whose not-sent vendors were left selected on this page for a retry
  const [keptSelectionJobIds, setKeptSelectionJobIds] = useState<Set<number>>(new Set());

  // Progress of a send running for the month shown (null when idle) — disables sending meanwhile
  const activeMonthSendJob = sendJobs.find((job) => isActiveSendJob(job) && job.monthYear === monthYear) ?? null;
  const invoiceSendProgress: InvoiceSendProgress | null = activeMonthSendJob
    ? {
      processed: activeMonthSendJob.rows.filter((row) => row.status !== 'PENDING').length,
      total: activeMonthSendJob.invoiceCount,
    }
    : null;

  // Fetch primary issuer info (for NSP invoice PDF generation)
  const { data: primaryIssuerData } = useQuery(NSP_CORE_INFO_BY_TYPE, {
    variables: { entryType: 'primary' },
    fetchPolicy: 'cache-first',
  });
  const primaryIssuer = primaryIssuerData?.nspCoreInfoByType ?? null;
  const hstRate = primaryIssuer?.hstRate ?? 0;

  // Fetch main data
  const { data, loading, refetch } = useQuery(INVOICES_BY_VENDOR, {
    variables: { monthYear },
    fetchPolicy: 'network-only',
  });

  // Initialize vendor data when data arrives
  useEffect(() => {
    if (data) {
      const vendors: VendorInvoice[] = data.invoicesByVendor;
      setVendorData(vendors);
      setOriginalData(vendors); // Store original data from server
    }
  }, [data]);

  // Refetch when monthYear changes (skip the initial mount — useQuery already
  // fires the network-only request for the initial monthYear)
  const isFirstRender = useRef(true);
  // Per vendor: the rows whose invoice email failed last time. A retry of that vendor sends only
  // these, so its rows that were already emailed don't go to the vendor a second time.
  const retryRowKeysByVendor = useRef(new Map<number, Set<string>>());
  useEffect(() => {
    retryRowKeysByVendor.current.clear();
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (monthYear) {
      refetch({ monthYear });
    }
  }, [monthYear, refetch]);

  const [updateInvoiceStatusBulkMutation, { loading: saving }] = useMutation(UPDATE_INVOICE_STATUS_BULK);
  const { logNsp: logNspDownload, logVendor: logVendorDownload } = useInvoiceDownloadAudit();

  const handleEdit = (vendorId: number, field: string, value: unknown) => {
    // Update the vendor data in the UI (using snake_case field names)
    setVendorData((prev) =>
      prev.map((vendor) =>
        vendor.vendor_id === vendorId ? { ...vendor, [field]: value } : vendor
      )
    );

    // Map snake_case field names to camelCase for GraphQL mutation
    const fieldMapping: Record<string, string> = {
      'invoice_status': 'invoiceStatus',
      'approval_status': 'approvalStatus',
      'invoice_approved': 'invoiceApproved',
      'invoice_computed_approval_signature': 'invoiceComputedApprovalSignature',
    };

    const mutationField = fieldMapping[field] || field;

    // Track pending changes (using camelCase for mutation)
    const key = `${vendorId}_${monthYear}`;
    setPendingChanges((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [mutationField]: value,
      } as InvoicePendingChanges,
    }));
  };

  const handleSave = async () => {
    const updates = Object.entries(pendingChanges)
      .map(([key, changes]) => {
        const [vendorIdStr] = key.split('_');
        const vendorId = Number(vendorIdStr);
        const original = originalData.find((v) => v.vendor_id === vendorId);

        // Drop fields that haven't changed from the server state
        const filteredChanges: InvoicePendingChanges = {};
        if ('invoiceStatus' in changes && changes.invoiceStatus !== original?.invoice_status) {
          filteredChanges.invoiceStatus = changes.invoiceStatus;
        }
        if ('approvalStatus' in changes && changes.approvalStatus !== original?.approval_status) {
          filteredChanges.approvalStatus = changes.approvalStatus;
        }
        if ('invoiceApproved' in changes && changes.invoiceApproved !== original?.invoice_approved) {
          filteredChanges.invoiceApproved = changes.invoiceApproved;
        }
        if ('invoiceComputedApprovalSignature' in changes && changes.invoiceComputedApprovalSignature !== original?.invoice_computed_approval_signature) {
          filteredChanges.invoiceComputedApprovalSignature = changes.invoiceComputedApprovalSignature;
        }

        if (Object.keys(filteredChanges).length === 0) return null;

        return {
          ippId: vendorId,
          monthYear: monthYear!,
          ...filteredChanges,
        };
      })
      .filter((u): u is NonNullable<typeof u> => u !== null);

    if (!updates.length) {
      message.info('No changes to save.');
      return;
    }

    setSaveFeedback(null);

    try {
      const { data } = await updateInvoiceStatusBulkMutation({ variables: { updates } });
      if (data.updateInvoiceStatusBulk.success) {
        setPendingChanges({});
        await refetch();
        setSaveFeedback({
          status: 'success',
          message: `Saved ${updates.length} invoice(s) successfully!`,
        });
        message.success(`Saved ${updates.length} invoice(s) successfully!`);
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to save updates' });
      message.error(err.message || 'Failed to save updates');
    }
  };

  // Batch create vendor invoices — queues one background send on the server, which builds each
  // vendor's PDF and emails it in groups (only IDs are sent, no PDFs). The send keeps going if the
  // page is refreshed or closed; its banner shows progress and the result.
  // Resolves, once the send finishes while this page is open, to the vendor IDs to keep selected
  // for a retry (email failed or never processed).
  const handleCreateVendorInvoices = async (vendorIds: Set<number>): Promise<number[]> => {
    if (!monthYear) return [];
    const rowKeyOf = (v: VendorInvoice) => invoiceRowKey(v.vendor_id!, vendorPrimaryName(v));
    const selectedRows = vendorData.filter((v) => v.vendor_id != null && vendorIds.has(v.vendor_id));
    // A vendor whose last send partly failed: only its failed rows go again. If none of those rows
    // exist any more (the data changed since), all of its rows are sent.
    const vendors = selectedRows.filter((v) => {
      const retryKeys = retryRowKeysByVendor.current.get(v.vendor_id!);
      if (!retryKeys) return true;
      const anyRetryRowPresent = selectedRows.some((r) => r.vendor_id === v.vendor_id && retryKeys.has(rowKeyOf(r)));
      return !anyRetryRowPresent || retryKeys.has(rowKeyOf(v));
    });
    if (!vendors.length) return [];

    setSaveFeedback(null);

    const updates: VendorInvoiceUpdate[] = vendors.map((v) => {
      const primaryVendorName = vendorPrimaryName(v);
      return {
        ippId: v.vendor_id!,
        monthYear,
        invoiceStatus: 'sent',
        approvalStatus: 'Approved',
        ...(primaryVendorName ? { primaryVendorName } : {}),
      };
    });

    // One id for this click: queuing the same click twice (e.g. a retried request) is a no-op.
    const sendRunId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    let job: VendorInvoiceSendJob | null;
    try {
      job = await startSendJob(updates, sendRunId);
    } catch (err: any) {
      // Nothing was queued — keep the selection so the user can try again.
      console.error('Failed to start vendor invoice send:', err);
      message.error(err?.message || 'Failed to start sending vendor invoices.');
      return [...new Set(vendors.map((v) => v.vendor_id!))];
    }
    if (!job) {
      message.error('Failed to start sending vendor invoices.');
      return [...new Set(vendors.map((v) => v.vendor_id!))];
    }

    message.info('Sending vendor invoices in the background — you can leave this page; the banner shows the result.');
    const queuedJobId = job.jobId;
    return new Promise<number[]>((resolve) => {
      inPageSends.current.set(queuedJobId, { vendors, sendRunId, startedAt: Date.now(), resolve });
    });
  };

  // A send started from this page finished: mirror the server's result in the table (the server
  // only marks an invoice "sent" once its email is delivered; a failed email leaves it Approved),
  // remember failed rows for a retry, download the ZIP, and hand back what to keep selected.
  const finishInPageSend = async (job: VendorInvoiceSendJob, { vendors, sendRunId, startedAt, resolve }: InPageSend) => {
    const jobMonth = job.monthYear;
    const rowKeyOf = (v: VendorInvoice) => invoiceRowKey(v.vendor_id!, vendorPrimaryName(v));
    const rowByKey = new Map(job.rows.map((row) => [invoiceRowKey(row.ippId, row.primaryVendorName), row]));
    // A row with no email outcome (NOT_PROCESSED) was never processed
    const outcomeOf = (v: VendorInvoice) => {
      const row = rowByKey.get(rowKeyOf(v));
      return row && row.status !== 'NOT_PROCESSED' && row.status !== 'PENDING' ? row : undefined;
    };
    const sentRows = vendors.filter((v) => outcomeOf(v)?.status === 'SENT');
    const notRecordedRows = vendors.filter((v) => outcomeOf(v)?.status === 'SENT_NOT_RECORDED');
    const failedRows = vendors.filter((v) => outcomeOf(v)?.status === 'FAILED');
    const unprocessedRows = vendors.filter((v) => !outcomeOf(v));
    const processedVendorIds = new Set(vendors.filter((v) => outcomeOf(v)).map((v) => v.vendor_id!));
    // SENT means the server marked the vendor's (shared) invoice record Sent — every row of it.
    const sentVendorIds = new Set(sentRows.map((v) => v.vendor_id!));

    appInsights?.trackEvent({
      name: 'VendorInvoiceSendCompleted',
      properties: {
        sendRunId,
        jobId: job.jobId,
        monthYear: jobMonth,
        total: vendors.length,
        sent: sentRows.length,
        failed: failedRows.length,
        notRecorded: notRecordedRows.length,
        unprocessed: unprocessedRows.length,
        stoppedEarly: Boolean(job.errorMessage),
        durationMs: Date.now() - startedAt,
      },
    });

    processedVendorIds.forEach((vendorId) => retryRowKeysByVendor.current.delete(vendorId));
    failedRows.forEach((v) => {
      const keys = retryRowKeysByVendor.current.get(v.vendor_id!) ?? new Set<string>();
      keys.add(rowKeyOf(v));
      retryRowKeysByVendor.current.set(v.vendor_id!, keys);
    });

    if (processedVendorIds.size && jobMonth === monthYear) {
      setVendorData((prev) =>
        prev.map((v) => {
          if (v.vendor_id == null || !processedVendorIds.has(v.vendor_id)) return v;
          return sentVendorIds.has(v.vendor_id)
            ? { ...v, invoice_status: 'sent', approval_status: 'Approved' }
            : { ...v, approval_status: 'Approved' };
        })
      );
    }

    // ZIP holds only the invoices that actually went out to vendors
    const emailedRows = [...sentRows, ...notRecordedRows];
    if (emailedRows.length) {
      try {
        await generateVendorInvoicesZip({ vendors: emailedRows, primaryIssuer, monthYear: jobMonth, hstRate });
        emailedRows.forEach((v) => {
          logVendorDownload({ ippId: v.vendor_id!, monthYear: jobMonth, vendorName: v.vendor_name });
        });
      } catch (err: any) {
        console.error('Failed to generate vendor invoices ZIP:', err);
        message.error('Invoices were sent, but the ZIP download failed.');
      }
    }

    if (!failedRows.length && !notRecordedRows.length && !unprocessedRows.length) {
      message.success(`${vendors.length} vendor invoice(s) sent successfully.`);
      resolve([]);
      return;
    }

    const notSentCount = failedRows.length + unprocessedRows.length;
    if (notSentCount) message.error(`${notSentCount} vendor invoice(s) were not sent.`);
    else message.warning(`${notRecordedRows.length} vendor invoice(s) were emailed but not marked as Sent.`);

    // Vendor IDs to keep selected so the user can retry them straight away
    const keepSelected = [...new Set([...failedRows, ...unprocessedRows].map((v) => v.vendor_id!))];
    if (keepSelected.length) setKeptSelectionJobIds((prev) => new Set(prev).add(job.jobId));
    resolve(keepSelected);
  };

  // React to sends finishing. One started here runs finishInPageSend; one started elsewhere (or
  // before a refresh) only reloads the table if it finished while this page was watching it.
  const watchedActiveJobIds = useRef(new Set<number>());
  useEffect(() => {
    let reloadTable = false;
    sendJobs.forEach((job) => {
      if (isActiveSendJob(job)) {
        watchedActiveJobIds.current.add(job.jobId);
        return;
      }
      const wasWatched = watchedActiveJobIds.current.delete(job.jobId);
      const inPageSend = inPageSends.current.get(job.jobId);
      if (inPageSend) {
        inPageSends.current.delete(job.jobId);
        finishInPageSend(job, inPageSend);
      } else if (wasWatched && job.monthYear === monthYear) {
        reloadTable = true;
      }
    });
    if (reloadTable) refetch();
  }, [sendJobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Banner "Download ZIP" for a finished send of the month shown: the invoices it emailed.
  const downloadVendorInvoiceSendZip = async (job: VendorInvoiceSendJob) => {
    if (job.monthYear !== monthYear) return;
    const emailedKeys = new Set(job.rows
      .filter((row) => row.status === 'SENT' || row.status === 'SENT_NOT_RECORDED')
      .map((row) => invoiceRowKey(row.ippId, row.primaryVendorName)));
    const emailedRows = vendorData.filter((v) => v.vendor_id != null && emailedKeys.has(invoiceRowKey(v.vendor_id, vendorPrimaryName(v))));
    if (!emailedRows.length) return;
    try {
      await generateVendorInvoicesZip({ vendors: emailedRows, primaryIssuer, monthYear, hstRate });
      emailedRows.forEach((v) => {
        logVendorDownload({ ippId: v.vendor_id!, monthYear, vendorName: v.vendor_name });
      });
    } catch (err: any) {
      console.error('Failed to generate vendor invoices ZIP:', err);
      message.error('Failed to download the vendor invoices ZIP.');
    }
  };

  const dismissVendorInvoiceSendJob = async (jobId: number) => {
    try {
      await dismissSendJob(jobId);
    } catch (err: any) {
      console.error('Failed to dismiss vendor invoice send:', err);
      message.error(err?.message || 'Failed to close the banner.');
    }
  };

  // Result text for a send's banner (names from the table when it shows that send's month)
  const getVendorInvoiceSendSummary = (job: VendorInvoiceSendJob) =>
    summarizeVendorInvoiceSend(job, job.monthYear === monthYear ? vendorData : [], {
      keptSelected: keptSelectionJobIds.has(job.jobId),
    });

  // Generate NSP invoice PDFs for selected vendors, zip them, and download
  const handleCreateNspInvoices = async (vendorIds: Set<number>) => {
    const vendors = vendorData.filter((v) => v.vendor_id != null && vendorIds.has(v.vendor_id));
    if (!vendors.length) return;

    try {
      await generateNspInvoicesZip({ vendors, primaryIssuer, monthYear: monthYear!, hstRate });

      vendors.forEach((v) => {
        logNspDownload({ ippId: v.vendor_id!, monthYear: monthYear!, vendorName: v.vendor_name });
      });

      message.success(`${vendors.length} NSP invoice(s) downloaded as ZIP.`);
    } catch (err: any) {
      message.error(err.message || 'Failed to generate NSP invoices.');
    }
  };

  // True when there is at least one pending change that differs from the server state
  const hasRealChanges = Object.entries(pendingChanges).some(([key, changes]) => {
    const vendorId = Number(key.split('_')[0]);
    const original = originalData.find((v) => v.vendor_id === vendorId);
    return (
      ('invoiceStatus' in changes && changes.invoiceStatus !== original?.invoice_status) ||
      ('approvalStatus' in changes && changes.approvalStatus !== original?.approval_status) ||
      ('invoiceApproved' in changes && changes.invoiceApproved !== original?.invoice_approved) ||
      ('invoiceComputedApprovalSignature' in changes && changes.invoiceComputedApprovalSignature !== original?.invoice_computed_approval_signature)
    );
  });

  // Helper function to check if a vendor was originally approved (from saved server data)
  const isVendorApprovalPersisted = (vendorId: number): boolean => {
    const originalVendor = originalData.find((vendor) => vendor.vendor_id === vendorId);
    return Boolean(originalVendor?.invoice_approved);
  };

  return {
    data: vendorData,
    loading,
    saving,
    saveFeedback,
    setSaveFeedback,
    invoiceSendProgress,
    vendorInvoiceSendJobs: sendJobs,
    dismissVendorInvoiceSendJob,
    downloadVendorInvoiceSendZip,
    getVendorInvoiceSendSummary,
    handleEdit,
    handleSave,
    handleCreateVendorInvoices,
    handleCreateNspInvoices,
    refetch,
    isVendorApprovalPersisted,
    hasRealChanges,
    username: account?.name || account?.username || '',
  };
};

export const useUpdateInvoiceStatus = () => {
  const [updateInvoiceStatusMutation, { loading }] = useMutation(UPDATE_INVOICE_STATUS);

  const updateInvoiceStatus = async ({
    ippId,
    monthYear,
    invoiceStatus,
    approvalStatus,
    invoiceApproved,
    invoiceComputedApprovalSignature,
  }: {
    ippId: number;
    monthYear: string;
    invoiceStatus?: string;
    approvalStatus?: string;
    invoiceApproved?: boolean;
    invoiceComputedApprovalSignature?: string;
  }) => {
    return await updateInvoiceStatusMutation({
      variables: {
        ippId,
        monthYear,
        invoiceStatus,
        approvalStatus,
        invoiceApproved,
        invoiceComputedApprovalSignature,
      },
    });
  };

  return {
    updateInvoiceStatus,
    loading,
  };
};
